import pako from "pako";
import { AWS_IMAGE_UPLOAD } from "../env";

// Tag prefix marks a gzip+base64url encoded prefill payload. Bumping the
// digit lets us evolve the encoding without breaking in-flight redirects.
const PREFILL_PREFIX = "z1.";

const bytesToBinaryString = (bytes) => {
    // Chunked to avoid call-stack overflows on large Uint8Arrays.
    const CHUNK = 0x8000;
    let out = "";
    for (let i = 0; i < bytes.length; i += CHUNK) {
        out += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return out;
};

const SIGNED_URL_REQUEST_TIMEOUT_MS = 30_000;
const S3_UPLOAD_TIMEOUT_MS = 5 * 60_000;

// Mirrors portal's `src/utils/sanitizeFilename.ts` byte-for-byte so the file
// path on S3 matches the path the portal reconstructs after restore. Any
// divergence here produces broken thumbnail URLs in the activity feed.
const sanitizeFilename = (name) => {
    try {
        const filename = String(name || "");
        let decoded = filename;
        try {
            decoded = decodeURIComponent(filename);
        } catch {
            // malformed percent-encoding — keep as-is
        }
        const dotIndex = decoded.lastIndexOf(".");
        const ext = dotIndex !== -1 ? decoded.slice(dotIndex).toLowerCase() : "";
        const base = dotIndex !== -1 ? decoded.slice(0, dotIndex) : decoded;
        const sanitized = base
            .replace(/[/\\]/g, "_")
            .replace(/[^a-zA-Z0-9_\-]/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "");
        return (sanitized || Date.now().toString()) + ext;
    } catch {
        return String(name || "");
    }
};

const isLocalApiEndpoint = (url) =>
    typeof url === "string" && /https?:\/\/(localhost|127\.0\.0\.1)/i.test(url);

const generateUid = () =>
    `rc-upload-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Upload one File to S3 via the signed-URL lambda. Resolves with metadata the
// hero needs to later restore the file on the portal:
//   { uid, name, image_uri, url, contentType, size }
export const uploadHeroProductImage = (file, { onProgress } = {}) => {
    return new Promise((resolve, reject) => {
        const uid = generateUid();
        const cleanName = sanitizeFilename(file.name);
        const fileKey = `test_files/${uid}/${cleanName}`;
        const contentType = file.type || "image/png";

        const controller = new AbortController();
        const timeoutId = window.setTimeout(
            () => controller.abort(new Error("Timed out while requesting upload URL")),
            SIGNED_URL_REQUEST_TIMEOUT_MS
        );

        fetch(AWS_IMAGE_UPLOAD, {
            method: "POST",
            headers: {
                "Content-Type": isLocalApiEndpoint(AWS_IMAGE_UPLOAD)
                    ? "text/plain;charset=UTF-8"
                    : "application/json",
            },
            signal: controller.signal,
            body: JSON.stringify({
                action: "get_asigned_urls",
                file_uri: fileKey,
                content_type: contentType,
            }),
        })
            .finally(() => window.clearTimeout(timeoutId))
            .then(async (res) => {
                if (!res.ok) throw new Error(`Failed to get signed URL (${res.status})`);
                const { upload_url, fields, file_url } = await res.json();
                if (!upload_url || !fields) throw new Error("Malformed signed-URL response");

                const formData = new FormData();
                Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
                formData.append("file", file, cleanName);

                const xhr = new XMLHttpRequest();
                xhr.open("POST", upload_url, true);
                xhr.timeout = S3_UPLOAD_TIMEOUT_MS;

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        onProgress(Math.round((event.loaded / event.total) * 100));
                    }
                };

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve({
                            uid,
                            name: cleanName,
                            image_uri: fileKey,
                            url: file_url || "",
                            contentType,
                            size: file.size || 0,
                        });
                    } else {
                        reject(new Error(`S3 upload failed with status ${xhr.status}`));
                    }
                };
                xhr.onerror = () => reject(new Error("S3 upload network error"));
                xhr.ontimeout = () => reject(new Error("S3 upload timed out"));
                xhr.send(formData);
            })
            .catch((err) => reject(err));
    });
};

// Encode a JS object into a URL-safe, gzip-compressed base64 string. Used to
// attach the hero prefill payload to the portal redirect URL. The portal-side
// decoder lives in all3d-portal-frontend-migrated/src/features/AiAssetCreate/
// hooks/useUrlState.ts and detects the "z1." prefix to inflate.
export const encodePrefillPayload = (payload) => {
    const json = JSON.stringify(payload);
    const utf8 = new TextEncoder().encode(json);
    const deflated = pako.deflateRaw(utf8, { level: 9 });
    const b64 = btoa(bytesToBinaryString(deflated))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");
    return `${PREFILL_PREFIX}${b64}`;
};

export const decodePrefillPayload = (encoded) => {
    if (!encoded) return null;
    try {
        const str = String(encoded);
        if (str.startsWith(PREFILL_PREFIX)) {
            const body = str.slice(PREFILL_PREFIX.length)
                .replace(/-/g, "+")
                .replace(/_/g, "/");
            const padded = body + "=".repeat((-body.length) & 3);
            const bin = atob(padded);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const json = pako.inflateRaw(bytes, { to: "string" });
            return JSON.parse(json);
        }
        // Legacy un-prefixed (raw base64-JSON) payloads — kept so any handcrafted
        // or in-flight links still decode.
        let b64 = str.replace(/-/g, "+").replace(/_/g, "/");
        while (b64.length % 4) b64 += "=";
        const json = decodeURIComponent(escape(atob(b64)));
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
};
